/**
 * @name rfidUtils
 * @description Utilitários para processamento de códigos RFID
 * @author Keven Adan Bezerra | kevenadanb
 * @package src/utils/rfidUtils.ts
 */

/**
 * Remove zeros à esquerda de códigos RFID
 * @param codigo - Código RFID original
 * @returns Código RFID sem zeros à esquerda
 *
 * @example
 * removeLeadingZeros('000123') // retorna '123'
 * removeLeadingZeros('0000FC002') // retorna 'FC002'
 * removeLeadingZeros('123456') // retorna '123456'
 * removeLeadingZeros('00000') // retorna '0' (mantém pelo menos um zero)
 */
export const removeLeadingZeros = (codigo: string): string => {
  if (!codigo || typeof codigo !== "string") {
    return codigo;
  }

  // Remove espaços em branco
  const codigoLimpo = codigo.trim();

  // Se o código está vazio após trim, retorna string vazia
  if (!codigoLimpo) {
    return "";
  }

  // Remove zeros à esquerda, mas mantém pelo menos um caractere
  const semZerosEsquerda = codigoLimpo.replace(/^0+/, "");

  // Se ficou vazio (só tinha zeros), retorna um zero
  return semZerosEsquerda || "0";
};

/**
 * Valida se um código parece ser um RFID válido
 * @param codigo - Código a ser validado
 * @returns true se parece ser um RFID válido
 */
export const isValidRfidFormat = (codigo: string): boolean => {
  if (!codigo || typeof codigo !== "string") {
    return false;
  }

  const codigoLimpo = codigo.trim();

  // RFID deve ter pelo menos 3 caracteres e no máximo 20
  if (codigoLimpo.length < 3 || codigoLimpo.length > 20) {
    return false;
  }

  // Pode conter letras, números e alguns caracteres especiais comuns em RFID
  const rfidPattern = /^[A-Za-z0-9]+$/;
  return rfidPattern.test(codigoLimpo);
};

/**
 * Processa um código de entrada, removendo zeros à esquerda e validando
 * @param codigo - Código de entrada
 * @returns Objeto com código processado e informações de validação
 */
export const processRfidCode = (codigo: string) => {
  const original = codigo;
  const cleaned = removeLeadingZeros(codigo);
  const isValid = isValidRfidFormat(cleaned);

  return {
    original,
    cleaned,
    isValid,
    hasLeadingZeros: original !== cleaned && original.startsWith("0"),
  };
};

export default {
  removeLeadingZeros,
  isValidRfidFormat,
  processRfidCode,
};
